/**
 * Platform-agnostic registration point for an access-token refresh function.
 *
 * Each platform (mobile / web) may register its own implementation that
 * refreshes the Okta access token and syncs it back into Redux via
 * `setCredentials`. The base API interceptor uses this to retry a single
 * 401 request after refreshing the token, before falling back to the
 * re-authentication modal.
 *
 * If no refresher is registered, the interceptor preserves its previous
 * behavior (immediately surfacing the auth modal on 401).
 */
export type AccessTokenRefresher = () => Promise<string | null>;

let registeredRefresher: AccessTokenRefresher | null = null;
let inFlightRefresh: Promise<string | null> | null = null;

// Up to N attempts when the refresher returns null / throws. This guards
// against transient Android failures (network hiccup, Okta endpoint cold
// start, SDK race when called concurrently from cold-start + AppState
// resume) so a one-off blip doesn't immediately surface the auth modal.
const REFRESH_MAX_ATTEMPTS = 3;
const REFRESH_BACKOFF_MS = [0, 200, 1000];

const sleep = (ms: number): Promise<void> =>
    new Promise<void>((resolve) => {
        setTimeout(resolve, ms);
    });

export const registerAccessTokenRefresher = (refresher: AccessTokenRefresher | null): void => {
    registeredRefresher = refresher;
};

export const hasAccessTokenRefresher = (): boolean => registeredRefresher !== null;

/**
 * Single-flight wrapper around the registered refresher so concurrent 401
 * responses do not trigger multiple parallel refresh requests. Internally
 * retries up to `REFRESH_MAX_ATTEMPTS` with exponential backoff on null /
 * thrown failures before giving up.
 */
export const refreshAccessToken = (): Promise<string | null> => {
    if (!registeredRefresher) return Promise.resolve(null);
    if (inFlightRefresh) return inFlightRefresh;

    const refresher = registeredRefresher;
    inFlightRefresh = (async (): Promise<string | null> => {
        try {
            for (let attempt = 0; attempt < REFRESH_MAX_ATTEMPTS; attempt += 1) {
                const backoff = REFRESH_BACKOFF_MS[attempt] ?? 1000;
                // Sequential await is intentional: backoff retry must
                // wait for the previous attempt to fail before starting
                // the next. Parallel awaits would defeat the purpose and
                // could trip Okta refresh-token reuse detection.
                // eslint-disable-next-line no-await-in-loop
                if (backoff > 0) await sleep(backoff);
                try {
                    // eslint-disable-next-line no-await-in-loop
                    const token = await refresher();
                    if (token) return token;
                } catch {
                    // Swallow and retry — only the final null is reported.
                }
            }
            return null;
        } finally {
            inFlightRefresh = null;
        }
    })();

    return inFlightRefresh;
};
